import { useEffect, useState } from "react";

/**
 * ============================================================================
 * useErrorHandler HOOK
 * ============================================================================
 *
 * Captures async errors and unhandled promise rejections.
 * ErrorBoundary catches synchronous render errors, but not:
 * - Async/await errors
 * - Promise rejections
 * - setTimeout/setInterval errors
 * - Event listener errors
 *
 * This hook captures those and re-throws them in a way that ErrorBoundary can catch.
 *
 * Usage:
 *   In your root component, call this hook once:
 *
 *   function App() {
 *     useErrorHandler();
 *     return <YourApp />;
 *   }
 *
 * ============================================================================
 */
export function useErrorHandler() {
  const [, setError] = useState();

  useEffect(() => {
    // Handle uncaught promise rejections
    const handleUnhandledRejection = (event) => {
      console.error("Unhandled Promise Rejection:", event.reason);

      // Re-throw to let it propagate
      setError(() => {
        throw event.reason;
      });
    };

    // Handle global errors
    const handleError = (event) => {
      console.error("Global Error:", event.error);

      // Re-throw to let it propagate
      setError(() => {
        throw event.error;
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
    };
  }, []);
}

/**
 * ============================================================================
 * useAsyncError HOOK
 * ============================================================================
 *
 * Utility hook to safely throw errors from async contexts.
 * Can be used in async effects or event handlers.
 *
 * Usage in an async effect:
 *   function MyComponent() {
 *     const throwAsyncError = useAsyncError();
 *
 *     useEffect(() => {
 *       fetchData().catch(throwAsyncError);
 *     }, []);
 *
 *     return <div>Content</div>;
 *   }
 *
 * Usage in event handler:
 *   function MyComponent() {
 *     const throwAsyncError = useAsyncError();
 *
 *     const handleClick = async () => {
 *       try {
 *         await someAsyncOperation();
 *       } catch (error) {
 *         throwAsyncError(error);
 *       }
 *     };
 *
 *     return <button onClick={handleClick}>Click</button>;
 *   }
 *
 * ============================================================================
 */
export function useAsyncError() {
  const [, setError] = useState();

  return (error) => {
    setError(() => {
      throw error;
    });
  };
}
